import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Lock01 } from "@untitledui/icons";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod/v3";
import { useLoginTransition } from "@/components/application/login-transition/login-transition";
import { AuthSplitLayout } from "@/components/auth/auth-split-layout";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/providers/auth-provider";
import { twoFactorRoute } from "@/router";
import { safeRedirectPath } from "@/utils/safe-redirect";
import { challengeSchema, formatBackupCode, INVALID_TOTP_CODE } from "@/utils/two-factor";

type ChallengeValues = z.infer<typeof challengeSchema>;

const INVALID_BACKUP_CODE = "That backup code isn't valid. Codes are 10 characters (XXXXX-XXXXX) and each works once.";

const verify = async ({ mode, code, trustDevice }: ChallengeValues) => {
  if (mode === "totp") return (await authClient.twoFactor.verifyTotp({ code, trustDevice })).error;
  return (await authClient.twoFactor.verifyBackupCode({ code: formatBackupCode(code) })).error;
};

export const TwoFactorChallenge = () => {
  const navigate = useNavigate();
  const { onSignIn } = useAuth();
  const { start: startLoginTransition } = useLoginTransition();
  const search = twoFactorRoute.useSearch();
  const [showRecovery, setShowRecovery] = useState(false);
  const form = useForm<ChallengeValues>({
    resolver: zodResolver(challengeSchema),
    defaultValues: { mode: "totp", code: "", trustDevice: false },
  });
  const mode = form.watch("mode");
  const isTotp = mode === "totp";
  const verifying = form.formState.isSubmitting;

  const submit = async (values: ChallengeValues) => {
    const verifyError = await verify(values);
    if (verifyError?.code === "INVALID_TWO_FACTOR_COOKIE") {
      navigate({ to: "/login", search: { next: search.next } });
      return;
    }
    if (verifyError) {
      if (verifyError.code === "INVALID_CODE") form.setError("code", { message: INVALID_TOTP_CODE });
      else if (verifyError.code === "INVALID_BACKUP_CODE") form.setError("code", { message: INVALID_BACKUP_CODE });
      else form.setError("code", { message: verifyError.message ?? "Verification failed. Please try again." });
      return;
    }

    await startLoginTransition();
    await onSignIn();
    navigate({ to: safeRedirectPath(search.next) });
  };

  const switchMode = () => form.reset({ mode: isTotp ? "backup" : "totp", code: "", trustDevice: false });

  return (
    <AuthSplitLayout>
      <form className="flex w-full max-w-[350px] flex-col gap-5" onSubmit={form.handleSubmit(submit)} noValidate>
        <div className="flex flex-col items-start gap-2 text-left">
          <span className="flex size-12 items-center justify-center rounded-full border border-gray-200 bg-unbox-cream-50">
            <Lock01 className="size-[22px] text-gray-700" aria-hidden />
          </span>
          <h1 className="text-lg font-semibold text-gray-900">Two-step verification</h1>
          <p className="text-sm text-gray-600">
            {isTotp
              ? "Enter the 6-digit code from your authenticator app."
              : "Enter one of your 10-character backup codes. Each code works once."}
          </p>
        </div>

        <Controller
          control={form.control}
          name="code"
          render={({ field, fieldState }) => (
            <Input
              key={mode}
              autoFocus
              label={isTotp ? "Authenticator code" : "Backup code"}
              placeholder={isTotp ? "123456" : "XXXXX-XXXXX"}
              inputMode={isTotp ? "numeric" : "text"}
              autoComplete="one-time-code"
              maxLength={isTotp ? 6 : 11}
              value={field.value}
              onChange={(value) =>
                field.onChange(isTotp ? value.replace(/\D/g, "").slice(0, 6) : formatBackupCode(value))
              }
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
              inputClassName="px-4 py-3 text-center font-mono text-xl tracking-[0.3em]"
            />
          )}
        />

        {isTotp && (
          <Controller
            control={form.control}
            name="trustDevice"
            render={({ field }) => (
              <Checkbox label="Trust this device for 30 days" isSelected={field.value} onChange={field.onChange} />
            )}
          />
        )}

        <Button type="submit" size="lg" isLoading={verifying} showTextWhileLoading>
          {verifying ? "Verifying…" : "Verify"}
        </Button>

        <div className="flex flex-col items-start gap-2 text-sm">
          <button
            type="button"
            className="cursor-pointer font-semibold text-brand-700 hover:underline"
            onClick={switchMode}
          >
            {isTotp ? "Use a backup code instead" : "Use your authenticator app instead"}
          </button>
          <button
            type="button"
            className="cursor-pointer text-gray-600 hover:text-gray-900 hover:underline"
            onClick={() => setShowRecovery((shown) => !shown)}
          >
            Lost access to your authenticator?
          </button>
          {showRecovery && (
            <p className="flex items-start gap-2.5 rounded-lg bg-warning-50 px-3.5 py-2.5 text-[13px] text-warning-700">
              <AlertTriangle className="mt-px size-[18px] shrink-0" aria-hidden />
              <span>
                Contact support from your account email at{" "}
                <a
                  href="mailto:support@unbox-commerce.com"
                  className="font-semibold underline decoration-dotted decoration-1 underline-offset-[3px] hover:decoration-solid"
                >
                  support@unbox-commerce.com
                </a>{" "}
                and we'll verify you and reset two-factor authentication. There's no self-serve recovery yet.
              </span>
            </p>
          )}
        </div>
      </form>
    </AuthSplitLayout>
  );
};
